import { useEffect } from "react";

import PropTypes from "prop-types";
import { Controller, useFormContext } from "react-hook-form";

import { Paper } from "@mui/material";
import Grid from "@mui/material/Grid";

import {
  AllOutletsSelect,
  OutletRoleEnum,
  UserOutletsSelect,
} from "@/features/outlets";
import { SupplierSelect } from "@/features/suppliers";
import { CustomDatePicker } from "@/features/ui";
import { OutletWarehouseSelect } from "@/features/warehouses";

export const GeneralInformationSection = ({ editMode = false }) => {
  const { control, watch, setValue } = useFormContext();

  const seller = watch("seller");
  const buyer = watch("buyer");
  const sellerableType = watch("sellerableType");

  useEffect(() => {
    if (editMode) return;

    if (buyer) {
      setValue("warehouse", null);
    }
  }, [buyer, editMode, setValue]);

  // Auto-set sellerableType based on buyer's role
  useEffect(() => {
    if (buyer) {
      const sellerableType =
        buyer.role === OutletRoleEnum.HUB ? "SUPPLIER" : "OUTLET";
      setValue("sellerableType", sellerableType);
    }
  }, [buyer, setValue]);

  useEffect(() => {
    if (editMode) return;

    if (sellerableType) {
      setValue("seller", null);
    }
  }, [sellerableType, editMode, setValue]);

  useEffect(() => {
    if (seller?.supplierTaxInformation) {
      setValue("taxable", seller.supplierTaxInformation.defaultTaxIncluded);
    }
    if (seller?.defaultPaymentTerm) {
      setValue("paymentTerm", seller.defaultPaymentTerm);
    }
  }, [seller, setValue]);

  return (
    <Paper sx={{ p: 2.5, borderRadius: 2 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="seller"
            render={({ field, fieldState }) =>
              sellerableType === "OUTLET" ? (
                <AllOutletsSelect
                  value={field.value}
                  onChange={field.onChange}
                  allowedRoles={[OutletRoleEnum.HUB]}
                  TextFieldProps={{
                    label: "Pemasok (Hub)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              ) : (
                <SupplierSelect
                  value={field.value}
                  onChange={field.onChange}
                  TextFieldProps={{
                    label: "Pemasok (Eksternal)",
                    required: true,
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                  }}
                />
              )
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="buyer"
            render={({ field, fieldState }) => (
              <UserOutletsSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode}
                TextFieldProps={{
                  label: "Outlet",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            control={control}
            name="warehouse"
            render={({ field, fieldState }) => (
              <OutletWarehouseSelect
                value={field.value}
                onChange={field.onChange}
                disabled={editMode}
                outletId={buyer?.id}
                TextFieldProps={{
                  label: "Gudang",
                  required: true,
                  error: fieldState.invalid,
                  helperText: fieldState.error?.message,
                  InputProps: {
                    placeholder: "Pilih Gudang",
                  },
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
          <Controller
            name="transactionDate"
            control={control}
            render={({ field, fieldState }) => (
              <CustomDatePicker
                value={field.value}
                onChange={field.onChange}
                required
                label="Tanggal Transaksi"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                disabled={editMode}
                InputProps={{
                  disableFuture: true,
                }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

GeneralInformationSection.propTypes = {
  editMode: PropTypes.bool,
};
